import {
  BadgeCellRenderer,
  TextButtonCellRenderer,
  type TypeBadgeCellRendererProps,
  type TypeTableColumn,
} from '@thefarmersfront/kitchen';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  RETURN_DISPOSAL_PROCESS_STATUS,
  RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR,
  type TypeReturnDisposalProcessStatus,
} from '~/constants';

import type { TypeReturnDisposalInfo } from '~/types/receive.types';

/** 처리상태 코드 → Badge 옵션 맵 (constants 두 객체를 합성) */
const PROCESS_STATUS_COLOR_MAP = (
  Object.keys(RETURN_DISPOSAL_PROCESS_STATUS) as TypeReturnDisposalProcessStatus[]
).reduce<NonNullable<TypeBadgeCellRendererProps<TypeReturnDisposalProcessStatus>['colorMap']>>(
  (acc, key) => ({
    ...acc,
    [key]: {
      label: RETURN_DISPOSAL_PROCESS_STATUS[key],
      color: RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR[key],
      variant: 'light',
      status: true,
    },
  }),
  {}
);

/**
 * [입고관리 > 회송/폐기 관리] 목록 테이블 컬럼 정의 (Kitchen Table)
 *
 * @note 체크박스(선택) 컬럼은 Kitchen `rowSelection`이 자동 삽입. 기본정렬(이슈등록일 내림차순)은 BE 응답 순서에 위임.
 */
const getReceiveReturnDisposalColumns = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (data: TypeReturnDisposalInfo) => void;
}): TypeTableColumn<TypeReturnDisposalInfo>[] => [
  {
    accessorKey: 'receiveRequestCode',
    header: '입고요청번호',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    id: 'detail',
    header: '',
    cell: (info) => (
      <TextButtonCellRenderer size="small" onClick={() => onClickViewDetail(info.row.original)}>
        상세보기
      </TextButtonCellRenderer>
    ),
    minSize: 88,
    meta: { columnFit: true, cellStyle: { textAlign: 'center' } },
  },
  {
    accessorKey: 'processMethodName',
    header: '처리방법',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'processStatus',
    header: '처리상태',
    meta: { columnFit: true },
    cell: (info) => {
      const value = info.getValue<TypeReturnDisposalProcessStatus>();
      return value ? <BadgeCellRenderer value={value} colorMap={PROCESS_STATUS_COLOR_MAP} /> : '-';
    },
  },
  {
    accessorKey: 'issueCenterName',
    header: '이슈발생센터',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issuedAt',
    header: '이슈등록일',
    meta: { columnFit: true },
    cell: (info) => formatDate(info.getValue<string>(), DATE_FORMAT.DATE) ?? '-',
  },
  {
    accessorKey: 'elapsedDays',
    header: '경과일수',
    cell: (info) => {
      const value = info.getValue<number>();
      return value || value === 0 ? `${formatNumber(value)}일` : '-';
    },
    meta: { columnFit: true, cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'scrapPlannedAt',
    header: '자동폐기예정일',
    meta: { columnFit: true },
    cell: (info) => formatDate(info.getValue<string | null>(), DATE_FORMAT.DATE) ?? '-',
  },
  {
    accessorKey: 'productCode',
    header: '상품코드',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'productName',
    header: '상품명',
    minSize: 100,
    cell: (info) => info.getValue<string>() || '-',
    meta: { flex: true },
  },
  {
    accessorKey: 'issueType',
    header: '이슈상세',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issueUnit',
    header: '이슈수량',
    cell: (info) => info.getValue<number | null>() ?? '-',
    meta: { columnFit: true, cellStyle: { textAlign: 'right' } },
  },
];

export default getReceiveReturnDisposalColumns;
